'use client';

import { Input } from '@app/app/_components/Input/Input';
import { Select } from '@app/app/_components/Input/Select/Select';
import { SelectOption } from '@app/app/_components/Input/Select/SelectOption';
import { SearchIcon } from '@app/app/_icons/SearchIcon';
import { Address } from '@app/domain/model/Address';
import { AddressAutocompleteResult } from '@app/infrastructure/address/model/AddressAutocompleteResult';
import { useAddressAutocomplete } from '@app/infrastructure/address/useAddressAutocomplete';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { generateId } from '@app/lib/id-generator/generateId';
import { twMerge } from 'tailwind-merge';

interface Props {
    countryOptions?: SelectOption[];
    namePrefix?: string;
    defaultValue?: Address;
    violations?: ViolationMap;
    onChange?: (address: Nullable<Address>) => void;
    defaultCountry?: string;
    disableCountrySelect?: boolean;
    showApartmentNumber?: boolean;
}

export function AddressSearchInput({
    countryOptions = [],
    namePrefix,
    defaultValue,
    violations,
    onChange,
    defaultCountry,
    disableCountrySelect = false,
    showApartmentNumber = true,
}: Props) {
    const t = useTranslations();
    const { search, results } = useAddressAutocomplete();

    const initialSelectedAddress = useMemo(() => {
        if (defaultValue) {
            return {
                id: generateId(),
                title: '',
                language: '',
                address: {
                    label: `${defaultValue.street || ''} ${defaultValue.houseNumber || ''}`.trim(),
                    countryCode: defaultValue.country || '',
                    countryName: '',
                    stateCode: '',
                    state: '',
                    countyCode: '',
                    county: defaultValue.municipality || '',
                    city: defaultValue.city || '',
                    district: defaultValue.municipality || '',
                    street: defaultValue.street || '',
                    houseNumber: defaultValue.houseNumber || undefined,
                    postalCode: defaultValue.postalCode || '',
                },
            };
        }
        return null;
    }, [defaultValue]);

    const [selectedAddress, setSelectedAddress] = useState<Nullable<AddressAutocompleteResult>>(initialSelectedAddress);
    const [selectedCountry, setSelectedCountry] = useState<Nullable<string>>(
        defaultValue?.country || defaultCountry || null,
    );
    const [apartmentNumber, setApartmentNumber] = useState(defaultValue?.apartmentNumber || '');

    const addressError = useMemo(() => {
        const cityField = namePrefix != null ? `${namePrefix}City` : 'city';
        const streetField = namePrefix != null ? `${namePrefix}Street` : 'street';
        const postalCodeField = namePrefix != null ? `${namePrefix}PostalCode` : 'postalCode';
        const houseNumberField = namePrefix != null ? `${namePrefix}HouseNumber` : 'houseNumber';

        return violations?.[cityField] != null ||
            violations?.[streetField] != null ||
            violations?.[postalCodeField] != null ||
            violations?.[houseNumberField] != null
            ? { message: 'address.validation.addressNotPreciseEnough' }
            : undefined;
    }, [violations, namePrefix]);

    const notifyAddressChange = useCallback(() => {
        if (!onChange) return;

        if (!selectedCountry) {
            onChange(null);
            return;
        }

        if (selectedAddress) {
            const address: Address = {
                govId: null,
                id: null,
                country: selectedCountry,
                street: selectedAddress.address.street || '',
                houseNumber: selectedAddress.address.houseNumber || '',
                apartmentNumber: apartmentNumber,
                city: selectedAddress.address.city || '',
                postalCode: selectedAddress.address.postalCode || '',
                municipality: selectedAddress.address.district || '',
                streetAddress: null,
                county: null,
                main: true,
            };
            onChange(address);
        } else {
            onChange(null);
        }
    }, [onChange, selectedCountry, selectedAddress, apartmentNumber]);

    useEffect(() => {
        notifyAddressChange();
    }, [notifyAddressChange]);

    const handleAddressSelected = useCallback(
        (addressId: string) => {
            const selected =
                addressId === initialSelectedAddress?.id
                    ? initialSelectedAddress
                    : (results.find(r => r.id === addressId) ?? null);
            setSelectedAddress(selected);
        },
        [results, initialSelectedAddress],
    );

    const handleSearch = useDebouncedCallback((searchQuery: string) => {
        void search(searchQuery, selectedCountry ?? undefined);
    }, 400);

    const handleCountrySelected = useCallback((country: string) => {
        setSelectedCountry(country);
    }, []);

    const handleApartmentNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setApartmentNumber(e.target.value);
    }, []);

    const addressSearchOptions = useMemo(() => {
        const options = results.map(result => ({
            value: result.id,
            label: result.address.label,
        }));

        if (initialSelectedAddress && results.length === 0) {
            options.unshift({
                value: initialSelectedAddress.id,
                label: initialSelectedAddress.address.label,
            });
        }

        return options;
    }, [results, initialSelectedAddress]);

    return (
        <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-4">
                {!disableCountrySelect && countryOptions.length > 0 && (
                    <div className="md:col-span-4">
                        <Select
                            name={namePrefix != null ? `${namePrefix}Country` : 'country'}
                            label={t('address.countryLabel')}
                            placeholder={t('address.countryPlaceholder')}
                            defaultValue={selectedCountry ?? undefined}
                            error={violations?.[namePrefix != null ? `${namePrefix}Country` : 'country']}
                            options={countryOptions}
                            onChange={handleCountrySelected}
                            noResultsHint={t('address.noCountryResultsHint')}
                            searchable
                        />
                    </div>
                )}

                {(disableCountrySelect || countryOptions.length === 0) && (
                    <input
                        type="hidden"
                        name={namePrefix != null ? `${namePrefix}Country` : 'country'}
                        value={selectedCountry ?? ''}
                    />
                )}

                <div className={twMerge('min-w-0 md:col-span-3', !showApartmentNumber && 'md:col-span-4')}>
                    <Select
                        name="addressSearch"
                        autoFocus
                        label={t('address.addressSearchLabel')}
                        placeholder={t('address.addressSearchPlaceholder')}
                        error={addressError}
                        options={addressSearchOptions}
                        defaultValue={selectedAddress?.id}
                        onChange={handleAddressSelected}
                        searchable
                        onSearch={handleSearch}
                        searchHint={t('address.typeToSearchHint')}
                        noResultsHint={t('address.noAddressResultsHint')}
                        rightIcon={<SearchIcon className="size-4" />}
                        disabled={selectedCountry == null}
                    />
                </div>

                {showApartmentNumber && (
                    <div className="md:col-span-1">
                        <Input
                            name={namePrefix != null ? `${namePrefix}ApartmentNumber` : 'apartmentNumber'}
                            label={t('address.apartmentNumberLabel')}
                            placeholder={t('address.apartmentNumberPlaceholder')}
                            value={apartmentNumber}
                            onChange={handleApartmentNumberChange}
                            error={
                                violations?.[namePrefix != null ? `${namePrefix}ApartmentNumber` : 'apartmentNumber']
                            }
                        />
                    </div>
                )}

                <input
                    type="hidden"
                    name={namePrefix != null ? `${namePrefix}Street` : 'street'}
                    value={selectedAddress?.address.street ?? ''}
                />
                <input
                    type="hidden"
                    name={namePrefix != null ? `${namePrefix}HouseNumber` : 'houseNumber'}
                    value={selectedAddress?.address.houseNumber ?? ''}
                />
                <input
                    type="hidden"
                    name={namePrefix != null ? `${namePrefix}PostalCode` : 'postalCode'}
                    value={selectedAddress?.address.postalCode ?? ''}
                />
                <input
                    type="hidden"
                    name={namePrefix != null ? `${namePrefix}City` : 'city'}
                    value={selectedAddress?.address.city ?? ''}
                />
                <input
                    type="hidden"
                    name={namePrefix != null ? `${namePrefix}Municipality` : 'municipality'}
                    value={selectedAddress?.address.district ?? ''}
                />
            </div>
        </div>
    );
}
